export const LIVE_TICKER_PREVIEW_DEBOUNCE_MS = 400;

export const tickerPreviewEnabledForEnvironment = (
  nodeEnv,
  tickerPreplanningEnabled = false,
) => nodeEnv === "development" || tickerPreplanningEnabled === true;

export const tickerPreviewQueryForDraft = (draft) =>
  String(draft || "").trim();

export const normalizeTickerPreviewRows = (rows) => {
  if (!Array.isArray(rows)) return [];

  const seenIds = new Set();
  return rows.filter((row) => {
    const id = row?.id == null ? "" : String(row.id).trim();
    const ticker = row?.ticker == null ? "" : String(row.ticker).trim();
    if (!id || !ticker || seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });
};

const finiteDimension = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
};

export const normalizeTickerPreviewAttachments = (attachments) => {
  if (!Array.isArray(attachments)) return [];

  return attachments.filter(Boolean).map((attachment) => {
    const file = attachment?.file || attachment?.blob || attachment;
    const filename = String(
      attachment?.filename || attachment?.name || file?.name || "",
    ).trim();
    const mime = String(
      attachment?.mime || file?.type || "application/octet-stream",
    )
      .split(";")[0]
      .trim()
      .toLowerCase();
    const sha256 = String(attachment?.sha256 || "").trim() || null;

    return {
      filename,
      mime: mime || "application/octet-stream",
      bytes: finiteDimension(attachment?.bytes ?? file?.size),
      width: finiteDimension(attachment?.width),
      height: finiteDimension(attachment?.height),
      sha256,
    };
  });
};

export const tickerPreviewDraftKey = ({
  threadUuid,
  query,
  attachments = [],
  editAnchorMessageId = null,
} = {}) =>
  JSON.stringify({
    threadUuid: threadUuid == null ? null : String(threadUuid),
    query: tickerPreviewQueryForDraft(query),
    attachments: normalizeTickerPreviewAttachments(attachments),
    editAnchorMessageId:
      editAnchorMessageId == null ? null : String(editAnchorMessageId),
  });

export const tickerPreviewRequestPayload = ({
  query,
  requestId,
  attachments = [],
  editAnchorMessageId = null,
} = {}) => {
  const data = {
    query: tickerPreviewQueryForDraft(query),
    request_id: requestId == null ? "" : String(requestId),
    attachments: normalizeTickerPreviewAttachments(attachments),
  };
  if (editAnchorMessageId != null) {
    data.edit_anchor_message_id = editAnchorMessageId;
  }
  return { data };
};

export const tickerPreviewCancelPayload = ({
  query = "",
  requestId = null,
} = {}) => ({
  data: {
    cancel: true,
    query: tickerPreviewQueryForDraft(query),
    request_id: requestId == null ? "" : String(requestId),
  },
});

export const cancelTickerPreviewGeneration = ({
  subscription,
  query = "",
  requestId = null,
} = {}) => {
  if (!subscription?.perform) return false;

  try {
    return subscription.perform(
      "preview_tickers",
      tickerPreviewCancelPayload({ query, requestId }),
    ) !== false;
  } catch (_error) {
    return false;
  }
};

export const tickerPreviewCleanupDisposition = ({
  previewSent = false,
  requestId = null,
  submittedRequestId = null,
} = {}) => {
  if (!previewSent) return "none";

  const request = String(requestId || "");
  const submitted = String(submittedRequestId || "");
  return request && request === submitted ? "preserve" : "cancel";
};

export const normalizeTickerPreviewPreplan = (preplan) => {
  const id = preplan?.id == null ? "" : String(preplan.id).trim();
  if (!id) return null;

  return {
    id,
    draft_digest:
      preplan?.draft_digest == null
        ? null
        : String(preplan.draft_digest),
    expires_at:
      preplan?.expires_at == null
        ? null
        : String(preplan.expires_at),
    status:
      preplan?.status == null ? null : String(preplan.status),
  };
};

export const tickerPreviewResultForEvent = (
  detail,
  activeThreadUuid,
  latestRequestId,
) => {
  if (detail?.threadUuid !== activeThreadUuid) return null;

  const preview = detail?.preview || {};
  if (
    String(preview.request_id || "") !==
    String(latestRequestId || "")
  ) {
    return null;
  }

  return {
    rows: normalizeTickerPreviewRows(preview.tickers),
    resolution: preview.resolution || null,
    preplan: normalizeTickerPreviewPreplan(preview.preplan),
  };
};

export const tickerPreviewRowsForEvent = (
  detail,
  activeThreadUuid,
  latestRequestId,
) => {
  const result = tickerPreviewResultForEvent(
    detail,
    activeThreadUuid,
    latestRequestId,
  );
  return result == null ? null : result.rows;
};
